import { useState, memo, useMemo, useContext } from 'react';
import { AccessibleDivButton, FavoriteIcon } from '@/components';
import { schoolCategories } from '@/mock/schoolCategoriesMock';
import { communityCategories } from '@/mock/communityCategoriesMock';
import { cx } from '@emotion/css';
import { Skeleton, Stack } from '@mui/material';
import schoolDefaultIcon from '@/assets/school_default_icon.svg';
import removedFlyerCardIcon from '@/assets/removed_flyer_card.svg';
import { FlyerType } from '@/config/constants';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { FlyerCardThumbnail } from '@/graphql/types';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import styles from './Card.styles';
import {
  BodyLargeEmphasized,
  BodyMedium,
  BodySmall,
  BodySmallMidEmphasis,
  Tag
} from '@/design-system';
import colors from '@/theme/colors';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { districtVar } from '@/graphql/state';
import { useReactiveVar } from '@apollo/client';

interface SavedFlyerCardProps {
  trackId: string;
  flyer: FlyerCardThumbnail;
  isExpired?: boolean;
  isRemoved?: boolean;
  unsaveFlyer: (flyerId: number) => void;
  dataTestId?: string;
}

const CardPure: React.FC<SavedFlyerCardProps> = ({
  trackId,
  flyer,
  isExpired = false,
  isRemoved = false,
  unsaveFlyer,
  dataTestId
}) => {
  const { community } = useContext(UserPreferencesContext);
  const district = useReactiveVar(districtVar);
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [loadingFlyer, setLoadingFlyer] = useState(true);
  const { pages } = flyer;
  const userText =  pages && pages[0] ? pages[0].userText : '';
  const onLoadFlyer = () => {
    setLoadingFlyer(false);
  };

  const disableFlyer = isExpired || isRemoved;

  const flyerCategories = useMemo(() =>
   flyer.type === FlyerType.school ?
     schoolCategories.filter(category => flyer.categories.includes(category.id))
    : communityCategories.filter(category => flyer.categories.includes(category.id))
  ,[flyer.categories, schoolCategories, communityCategories])

  const gradesOrAges = useMemo(() => {
    const { type, minTargetGradeOrAge, maxTargetGradeOrAge } = flyer;
    if (minTargetGradeOrAge && maxTargetGradeOrAge) {
      const prefix = type === FlyerType.community ? 'AGE' : 'GR'
      let gradeOrAge = '';
      if (minTargetGradeOrAge === maxTargetGradeOrAge) {
        gradeOrAge = minTargetGradeOrAge;
      }
      else {
        gradeOrAge = `${minTargetGradeOrAge}-${maxTargetGradeOrAge}`;
      }
      return `${prefix} ${gradeOrAge}`;
    }
    return null;
  }, [flyer]);


  const goToFlyerDetails = (flyerId: number, flyerTitle: string) => {
    if(isRemoved) return;
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:go_to_flyer_details`,
      label: flyerTitle,
      value: flyerId,
    });
    navigate(`/district/${district?.districtId}/flyer/${flyerId}`);
  };

  const handleUnsaveFlyer = () => {
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:unfavorite_flyer`,
      label: flyer.title,
      value: Number(flyer.flyerId),
    });
    unsaveFlyer(flyer.flyerId);
  };

  return (
    <div className={styles.wrapper} data-testid='SavedFlyerCard'>
    {loadingFlyer && (
      <div className={styles.loader}>
        <Stack spacing={1}>
          <div className={styles.container}>
            <div>
              <Skeleton variant="rectangular" width="125px" height="158px" />
            </div>
            <div className={styles.skeletonContent}>
              <Skeleton variant="text" height="30px" />
              <Skeleton variant="text" height="30px" />
              <Skeleton variant="text" height="30px" />
            </div>
          </div>
        </Stack>
      </div>
    )}

      <AccessibleDivButton
        className={cx(styles.container, disableFlyer ? styles.disabledFlyer : {})}
        style={loadingFlyer ? { display: 'none' } : {}}
        data-testid={dataTestId}
        key={flyer.flyerId}
        onClick={() => goToFlyerDetails(flyer.flyerId, flyer.title)}
        //  data-testid="savedFlyerContent"
      >
        <div className={styles.content}>
          <div className={styles.flyer}>
            <img
              onLoad={onLoadFlyer}
              src={isRemoved ? removedFlyerCardIcon : flyer.frontImageUrl}
              alt={`Image of a flyer titled ${flyer.title}`}
              aria-describedby={`flyer-Description-${flyer.flyerId}`}
              width="125px"
              height="158px"
              data-testid="flyerThumbnail"
            />
            <span
              id={`flyer-Description-${flyer.flyerId}`}
              className={styles.flyerDescription}
              data-testid="flyer-description"
            >
              Text Found On Flyer -
              {userText || 'No Text Available'} Need more information on this
              flyer? You can email the person who submitted the flyer at{' '}
              {flyer.email} to learn more.
            </span>
          </div>
          <div className={styles.body}>
            <div className={styles.orgName}>
            {flyer.type === FlyerType.school ? (
                <div className={styles.schoolIconContainer}>
                  <img
                    src={schoolDefaultIcon}
                    alt="school logo"
                    className={styles.schoolLogo}
                    width="24px"
                    height="24px"
                  />
                  {flyer?.schoolCount && flyer.schoolCount > 0 ? (
                    <BodySmall color={colors.slate} nonTranslate>{flyer.schoolCount}+ schools</BodySmall>
                  ) : null}
                </div>
                )
                : (
                  <BodySmallMidEmphasis color={colors.slate} nonTranslate>
                    {flyer.org?.name}
                  </BodySmallMidEmphasis>
                )
              }
            </div>
            <div>
          <BodyLargeEmphasized
            className={cx(styles.title, disableFlyer && styles.titleExpired)}
            nonTranslate
            data-testid="flyerTitle"
          >
            {flyer.title}
          </BodyLargeEmphasized>
          {isRemoved ? (
            <div className={styles.removedFlyerLabel}>
              <BodyMedium>This flyer is removed.</BodyMedium>
            </div>
          ) : (
            <>
              {flyerCategories.map((category, categoryIndex) => (
              <BodySmall color={colors.slate} data-testid="category" key={category.id}>
                {category.title}
                {categoryIndex < flyerCategories.length - 1 ? ' • ' : ''}
              </BodySmall>
              ))}
              <div className={styles.agesOrGradesContainer}>
                {gradesOrAges && (
                  <Tag variant='greyOutline'>
                    {gradesOrAges}
                  </Tag>
                )}
                {isExpired && (
                  <Tag variant='expired'>
                    Expired
                  </Tag>
                )}
              </div>
            </>
          )}
        </div>
          </div>
        </div>
      </AccessibleDivButton>
        {!loadingFlyer && (
          <AccessibleDivButton
            onClick={handleUnsaveFlyer}
            aria-label="Unsave favorite flyer button"
            style={{ height: '20px' }}
            className={styles.favoriteIcon}
            data-testid="unsaveFlyerButton"
          >
            <FavoriteIcon size={20} />
          </AccessibleDivButton>
        )}
  </div>
  );
};

export const Card = memo(CardPure);
